import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { varinymAPI } from '../lib/varinym-api';
import type { Context, Alias, CreatedAlias, ApiResponse } from '../types';

const TABS = [
  { path: '/identities', label: 'Create Identity' },
  { path: '/identities/add-alias', label: 'Add Alias' },
  { path: '/identities/search', label: 'Search/Resolve' },
] as const;

// Renders a compact relative expiry (e.g. "in 5d", "3d ago") for table columns
// where a full timestamp would take up too much width; the full date/time
// is meant to be shown as a title tooltip alongside it.
function formatExpiresRelative(expiresSeconds: number): { label: string; title: string } {
  const title = new Date(expiresSeconds * 1000).toLocaleString();
  const diffDays = Math.round((expiresSeconds * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { label: 'Today', title };
  if (diffDays > 0) return { label: `in ${diffDays}d`, title };
  return { label: `${Math.abs(diffDays)}d ago`, title };
}

export function Identities() {
  const location = useLocation();

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Identity Management</h1>

      {/* Tabs */}
      <div className="border-b border-border mb-6 overflow-x-auto overflow-y-hidden">
        <nav className="-mb-px flex space-x-8 min-w-max">
          {TABS.map((tab) => (
            <Link
              key={tab.path}
              to={tab.path}
              className={`link-bare ${
                location.pathname === tab.path
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      {location.pathname === '/identities/add-alias' && <AddAlias />}
      {location.pathname === '/identities/search' && <SearchIdentity />}
      {location.pathname !== '/identities/add-alias' && location.pathname !== '/identities/search' && <CreateIdentity />}
    </div>
  );
}

function AddAlias() {
  const [userId, setUserId] = useState('');
  const [debouncedUserId, setDebouncedUserId] = useState('');
  const [selectedContext, setSelectedContext] = useState('');
  const [result, setResult] = useState<any>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Debounce the existing-aliases lookup so it doesn't fire on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserId(userId.trim()), 400);
    return () => clearTimeout(timer);
  }, [userId]);

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(true),
  });

  // 'default' is created automatically for every identity, so it always
  // exists already — there's never a real reason to add it here.
  const addableContexts = (contextsData?.contexts ?? []).filter(
    (s: Context) => s.name.toLowerCase() !== 'default'
  );

  // Shows which contexts this identity already has a live alias for, so
  // the admin can see at a glance what's missing before picking one to add.
  const { data: existingAliasesData } = useQuery({
    queryKey: ['aliases', debouncedUserId],
    queryFn: () => varinymAPI.getAliases(debouncedUserId, true),
    enabled: !!debouncedUserId,
    retry: false,
  });

  const activeContexts = new Set(
    (existingAliasesData?.aliases ?? [])
      .filter((d) => d.alias_id !== '0' && (d.expires === 0 || d.expires > Date.now() / 1000))
      .map((d) => d.context)
  );

  useEffect(() => {
    if (addableContexts.length > 0 && !selectedContext) {
      setSelectedContext(addableContexts[0].name);
    }
  }, [contextsData]);

  const addMutation = useMutation({
    mutationFn: ({ userId, context }: { userId: string; context: string }) =>
      varinymAPI.addAlias(userId, context),
    onSuccess: (data) => {
      setResult({ ...data, context: selectedContext });
    },
    onError: (err: Error) => {
      setResult({ error: err.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userId.trim() && selectedContext) {
      setResult(null);
      addMutation.mutate({ userId: userId.trim(), context: selectedContext });
    }
  };

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const handleCopyAliasId = (aliasId: string) => {
    navigator.clipboard.writeText(aliasId);
    alert('Alias ID copied to clipboard!');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card title="Add Alias to Identity">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="User ID"
                placeholder="user@example.com"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                helperText="Enter the existing identity's user ID"
              />

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Context
                </label>
                <div className="flex flex-wrap gap-2">
                  {addableContexts.map((s: Context) => {
                    const isSelected = selectedContext === s.name;
                    const isActive = activeContexts.has(s.name);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedContext(s.name)}
                        className={`px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-muted text-foreground flex items-center gap-2'
                            : 'border-border bg-card text-card-foreground hover:border-primary/50 hover:bg-accent'
                        }`}
                      >
                        {isSelected && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px]">✓</span>
                        )}
                        {s.name}
                        {isActive && (
                          <span className="ml-1 text-xs text-muted-foreground">(active)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {contextsData?.contexts && addableContexts.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    No addable contexts — every identity already has a "default" alias automatically. Create another context first.
                  </p>
                )}
                {selectedContext && activeContexts.has(selectedContext) && (
                  <div className="mt-3 bg-muted border border-border rounded-md p-3 flex gap-2 items-start">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      This identity already has an active alias for <strong>{selectedContext}</strong> — submitting will return that existing alias ID rather than creating a new one.
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                isLoading={addMutation.isPending}
                disabled={!userId.trim() || !selectedContext}
              >
                Add Alias
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <div>
        <Card title="What is Add Alias?">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Use this when an identity already exists but needs an alias for a context it doesn't have yet — for example, onboarding it onto a new vendor integration.
            </p>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">Good to Know:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>The identity must already exist — this won't create one</li>
                <li>"default" isn't listed — every identity gets it automatically</li>
                <li>If an alias for this context already exists, its ID is returned unchanged</li>
                <li>Contexts marked "(active)" above already have a live alias</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {result && (
        <div className="lg:col-span-3" ref={resultRef}>
          <Card title={result.error ? 'Error' : result.existing ? 'Alias Already Existed' : 'Alias Added'}>
            {result.error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                <p className="text-destructive text-sm">{result.error}</p>
              </div>
            ) : (
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Context</dt>
                  <dd className="mt-1 text-sm text-foreground">{result.context}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Alias ID</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono break-all">
                    <div className="flex items-center gap-2">
                      <span>{result.alias_id}</span>
                      <button
                        onClick={() => handleCopyAliasId(result.alias_id)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Copy alias ID"
                        type="button"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Expires</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {result.expires === 0 ? 'Never' : new Date(result.expires * 1000).toLocaleString()}
                  </dd>
                </div>
              </dl>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function CreateIdentity() {
  const [userId, setUserId] = useState('');
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const [result, setResult] = useState<any>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(true),
  });

  // Auto-select 'default' context when contexts data loads
  useEffect(() => {
    if (contextsData?.contexts) {
      const defaultContext = contextsData.contexts.find((s: Context) => s.name.toLowerCase() === 'default');
      if (defaultContext && !selectedContexts.includes(defaultContext.name)) {
        setSelectedContexts([defaultContext.name]);
      }
    }
  }, [contextsData]);

  const createMutation = useMutation({
    mutationFn: ({ userId, contexts }: { userId: string; contexts: string[] }) => {
      // Filter out 'default' from the contexts list since it's always created by the API
      const additionalContexts = contexts.filter(s => s.toLowerCase() !== 'default');
      return varinymAPI.createIdentity(userId, additionalContexts.length > 0 ? additionalContexts : undefined);
    },
    onSuccess: (data) => {
      setResult(data);
      setUserId('');
    },
    onError: (err: Error) => {
      setResult({ error: err.message });
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (userId.trim() && selectedContexts.length > 0) {
      createMutation.mutate({ userId: userId.trim(), contexts: selectedContexts });
    }
  };

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const handleCopyAliasId = (aliasId: string) => {
    navigator.clipboard.writeText(aliasId);
    alert('Alias ID copied to clipboard!');
  };

  const toggleContext = (contextName: string) => {
    const isDefault = contextName.toLowerCase() === 'default';

    // Prevent deselecting 'default' context
    if (isDefault && selectedContexts.includes(contextName)) {
      return;
    }

    // Toggle context selection
    setSelectedContexts(prev =>
      prev.includes(contextName)
        ? prev.filter(s => s !== contextName)
        : [...prev, contextName]
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card title="Create New Identity">
          <form onSubmit={handleCreate}>
            <div className="space-y-4">
              <Input
                label="User ID"
                placeholder="user@example.com"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                helperText="Enter a unique user identifier (email, username, UUID, etc.)"
              />

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Contexts (select one or more)
                </label>
                <div className="flex flex-wrap gap-2">
                  {contextsData?.contexts?.map((s: Context) => {
                    const isSelected = selectedContexts.includes(s.name);
                    const isDefault = s.name.toLowerCase() === 'default';
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleContext(s.name)}
                        disabled={isDefault && isSelected}
                        className={`px-4 py-2 rounded-md border-2 font-medium text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-muted text-foreground flex items-center gap-2'
                            : 'border-border bg-card text-card-foreground hover:border-primary/50 hover:bg-accent'
                        } ${isDefault && isSelected ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {isSelected && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px]">✓</span>
                        )}
                        {s.name}
                        {isDefault && isSelected && (
                          <span className="ml-1 text-xs">(required)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {contextsData?.contexts && contextsData.contexts.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">No contexts available. Create a context first.</p>
                )}
                {selectedContexts.length > 0 && (
                  <div className="mt-3 bg-muted border border-border rounded-md p-3 flex gap-2 items-start">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      <strong>Note:</strong> Aliases will be automatically created for {selectedContexts.length} context{selectedContexts.length > 1 ? 's' : ''}: <strong>{selectedContexts.join(', ')}</strong>
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                isLoading={createMutation.isPending}
                disabled={!userId.trim() || selectedContexts.length === 0}
              >
                Create Identity
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <div>
        <Card title="What is an Identity?">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              An identity is a mapping between your user's ID and their internal record ID in the Varinym system.
            </p>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">Key Components:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><span className="font-medium">User ID:</span> Your system's identifier</li>
                <li><span className="font-medium">Record ID:</span> Internal Varinym identifier</li>
                <li><span className="font-medium">Alias ID:</span> Context-specific masked identifier</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {result && (
        <div className="lg:col-span-3" ref={resultRef}>
          <Card title={result.error ? "Error" : "Identity Created"}>
            {result.error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                <p className="text-destructive text-sm">{result.error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    User ID
                  </div>
                  <div className="text-foreground font-mono text-xs mt-1 break-all">{result.id}</div>
                </div>

                {result.aliases && result.aliases.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Alias{result.aliases.length > 1 ? 's' : ''} Created
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="min-w-full divide-y divide-border">
                        <thead>
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Context
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Alias ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Expires
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {result.aliases.map((alias: CreatedAlias) => (
                            <tr key={alias.alias_id} className="hover:bg-accent/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                {alias.context}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                                <div className="flex items-center gap-2">
                                  <span>{alias.alias_id}</span>
                                  <button
                                    onClick={() => handleCopyAliasId(alias.alias_id)}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    title="Copy alias ID"
                                    type="button"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                {alias.expires === 0 ? 'Never' : new Date(alias.expires * 1000).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function SearchIdentity() {
  const [mode, setMode] = useState<'identity' | 'alias'>('identity');
  const [inputValue, setInputValue] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchAliasId, setSearchAliasId] = useState('');
  // The alias ID that was actually searched for, so the matching row in
  // the table below can be highlighted once resolved to its owning identity.
  const [highlightAliasId, setHighlightAliasId] = useState('');
  const queryClient = useQueryClient();
  const resultRef = useRef<HTMLDivElement>(null);

  const { data: resolution, isLoading: isResolving, error: resolveError } = useQuery({
    queryKey: ['resolve', searchAliasId],
    queryFn: () => varinymAPI.resolveAlias(searchAliasId),
    enabled: !!searchAliasId,
  });

  // resolve_id only confirms the user_id — feed it back into the same
  // identity lookup used for a direct search, so both paths land on one view.
  useEffect(() => {
    if (searchAliasId && resolution?.user_id) {
      setSearchUserId(resolution.user_id);
      setHighlightAliasId(searchAliasId);
    }
  }, [searchAliasId, resolution]);

  const { data: identity } = useQuery({
    queryKey: ['identity', searchUserId],
    queryFn: () => varinymAPI.getIdentity(searchUserId),
    enabled: !!searchUserId,
  });

  const { data: aliases, isLoading: isLoadingAliases, error } = useQuery({
    queryKey: ['aliases', searchUserId],
    queryFn: () => varinymAPI.getAliases(searchUserId, true),
    enabled: !!searchUserId,
  });

  const expireMutation = useMutation({
    mutationFn: (aliasId: string) => varinymAPI.expireAlias(aliasId),
    onSuccess: (_data, aliasId) => {
      // Same edge-cache staleness as rotate (see varinymAPI.getAliases) —
      // patch the row locally instead of invalidating so it reflects the expiry
      // immediately rather than risking a stale refetch reshowing it as active.
      queryClient.setQueryData(
        ['aliases', searchUserId],
        (old?: ApiResponse<{ aliases: Alias[] }>) =>
          old && {
            ...old,
            aliases: old.aliases.map((d) =>
              d.alias_id === aliasId
                ? { ...d, expires: Math.floor(Date.now() / 1000) - 1 }
                : d
            ),
          }
      );
      alert('Alias expired successfully!');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (alias: Alias) =>
      alias.alias_id === '0'
        ? varinymAPI.deleteAliasByUserContext(searchUserId, alias.context)
        : varinymAPI.deleteAlias(alias.alias_id),
    onSuccess: (_data, alias) => {
      // Same edge-cache staleness as rotate — remove the row locally instead
      // of invalidating so a stale refetch can't reshow the deleted row.
      queryClient.setQueryData(
        ['aliases', searchUserId],
        (old?: ApiResponse<{ aliases: Alias[] }>) =>
          old && {
            ...old,
            aliases: old.aliases.filter(
              (d) => !(d.context === alias.context && d.alias_id === alias.alias_id)
            ),
          }
      );
      alert('Alias deleted successfully!');
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (context: string) => varinymAPI.rotateAlias(searchUserId, context),
    onSuccess: (response, context) => {
      // Patch the row directly from the rotate response instead of refetching:
      // get_aliases sits behind an edge cache (and the browser's own HTTP
      // cache) that can serve the pre-rotation value for up to ~30s, so an
      // invalidate-and-refetch here would risk immediately clobbering this
      // with stale data. The POST response is already the source of truth.
      queryClient.setQueryData(
        ['aliases', searchUserId],
        (old?: ApiResponse<{ aliases: Alias[] }>) =>
          old && {
            ...old,
            aliases: old.aliases.map((d) =>
              d.context === context
                ? { ...d, alias_id: response.alias_id, expires: response.expires }
                : d
            ),
          }
      );
      alert('Alias rotated successfully!');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = inputValue.trim();
    if (!value) return;
    if (mode === 'identity') {
      setSearchAliasId('');
      setHighlightAliasId('');
      setSearchUserId(value);
    } else {
      // Clear the identity results until resolve comes back, so the table
      // doesn't briefly show the previous search's stale data.
      setSearchUserId('');
      setHighlightAliasId('');
      setSearchAliasId(value);
    }
  };

  const handleModeChange = (newMode: 'identity' | 'alias') => {
    if (newMode === mode) return;
    setMode(newMode);
    setInputValue('');
    setSearchUserId('');
    setSearchAliasId('');
    setHighlightAliasId('');
  };

  const handleCopyAliasId = (aliasId: string) => {
    navigator.clipboard.writeText(aliasId);
    alert('Alias ID copied to clipboard!');
  };

  // The resolved alias may no longer show up in the identity's current
  // alias list (expired-and-masked, deleted, expired) — surface that
  // distinctly from "identity not found".
  const highlightedAlias = highlightAliasId
    ? aliases?.aliases?.find((d) => d.alias_id === highlightAliasId)
    : undefined;

  const isSearchPending = isResolving || isLoadingAliases;

  useEffect(() => {
    if ((searchUserId && (aliases || error)) || (searchAliasId && resolveError)) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchUserId, aliases, error, searchAliasId, resolveError]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Search">
          <form onSubmit={handleSearch}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Search by</label>
                <div className="inline-flex rounded-md border border-border p-1 bg-muted">
                  <button
                    type="button"
                    onClick={() => handleModeChange('identity')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      mode === 'identity' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Identity
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('alias')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      mode === 'alias' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Alias ID
                  </button>
                </div>
              </div>

              <Input
                label={mode === 'identity' ? 'User ID' : 'Alias ID'}
                placeholder={mode === 'identity' ? 'user@example.com' : 'd1b2c3d4-e5f6-7890-abcd-ef1234567890'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                helperText={
                  mode === 'identity'
                    ? 'Enter the user ID to look up their identity'
                    : 'Enter an alias ID to find the identity it belongs to'
                }
              />

              <Button
                type="submit"
                className="w-full"
                isLoading={isSearchPending}
                disabled={!inputValue.trim()}
              >
                {mode === 'identity' ? 'Search Identity' : 'Resolve Alias'}
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <div>
        <Card title="Search Tips">
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Look up an identity directly by its User ID, or resolve an Alias ID back to the identity it belongs to.
            </p>
            <div className="pt-3 border-t border-border">
              <p className="font-medium text-card-foreground mb-2">What You'll See:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>All aliases for the identity</li>
                <li>Which context each alias belongs to</li>
                <li>Alias status (Active/Expired)</li>
                <li>Options to expire, rotate, or delete aliases</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {searchAliasId && resolveError && (
        <div className="lg:col-span-3" ref={resultRef}>
          <Card title="Error">
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <p className="text-destructive text-sm">Alias ID not found or error occurred</p>
            </div>
          </Card>
        </div>
      )}

      {searchUserId && highlightAliasId && aliases && !highlightedAlias && (
        <div className="lg:col-span-3">
          <div className="bg-warning/10 border border-warning/20 rounded-md p-4 flex gap-2 items-start">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0" />
            <p className="text-sm text-foreground">
              This alias ID resolved to the identity below, but is no longer active — it may have expired or been deleted.
            </p>
          </div>
        </div>
      )}

      {searchUserId && identity && (
        <div className="lg:col-span-3">
          <Card title="Identity Details">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">User ID</dt>
                <dd className="mt-1 text-sm text-foreground font-mono break-all">{searchUserId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                <dd className="mt-1 text-sm text-foreground">{new Date(identity.created).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
                <dd className="mt-1 text-sm text-foreground">{new Date(identity.updated).toLocaleString()}</dd>
              </div>
            </dl>
          </Card>
        </div>
      )}

      {searchUserId && aliases && (
        <div className="lg:col-span-3" ref={resultRef}>
          <Card title={`Aliases for User: ${searchUserId}`}>
            {aliases?.aliases && aliases.aliases.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Alias ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Context
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {aliases.aliases.map((alias) => {
                        const isPermanentlyExpired = alias.alias_id === "0";
                        const isExpired = isPermanentlyExpired || (alias.expires !== 0 && alias.expires < Date.now() / 1000);
                        const isSearchedMatch = alias.alias_id === highlightAliasId;
                        return (
                          <tr key={alias.alias_id} className={`hover:bg-accent/50 ${isSearchedMatch ? 'bg-primary/5' : ''}`}>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                              <div className="flex items-center gap-2">
                                <span>{alias.alias_id.slice(0, 12)}...</span>
                                {isSearchedMatch && (
                                  <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 flex-shrink-0">
                                    Searched
                                  </span>
                                )}
                                {!isExpired && (
                                  <button
                                    onClick={() => handleCopyAliasId(alias.alias_id)}
                                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                    title="Copy full alias ID"
                                    type="button"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-foreground max-w-xs truncate" title={alias.context}>
                              {alias.context}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                  isExpired
                                    ? 'bg-destructive/10 text-destructive'
                                    : 'bg-success/10 text-success'
                                }`}
                              >
                                {isExpired ? 'Expired' : 'Active'}
                              </span>
                            </td>
                            <td
                              className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground truncate"
                              title={
                                isPermanentlyExpired
                                  ? 'Permanently Expired'
                                  : alias.expires === 0
                                    ? 'Never expires'
                                    : formatExpiresRelative(alias.expires).title
                              }
                            >
                              {isPermanentlyExpired
                                ? 'Permanently Expired'
                                : alias.expires === 0
                                  ? 'Never'
                                  : formatExpiresRelative(alias.expires).label}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                              {!isExpired && (
                                <button
                                  onClick={() => expireMutation.mutate(alias.alias_id)}
                                  className="text-warning hover:text-warning/80 transition-colors"
                                  disabled={expireMutation.isPending}
                                >
                                  Expire
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (confirm(
                                    `Are you sure you want to rotate this alias? This immediately generates a new alias ID for ${alias.context} — the current alias ID will stop resolving right away and cannot be recovered.`
                                  )) {
                                    rotateMutation.mutate(alias.context);
                                  }
                                }}
                                className="text-warning hover:text-warning/80 transition-colors"
                                disabled={rotateMutation.isPending}
                              >
                                Rotate
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this alias?')) {
                                    deleteMutation.mutate(alias);
                                  }
                                }}
                                className="text-destructive hover:text-destructive/80 transition-colors"
                                disabled={deleteMutation.isPending}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  {searchUserId ? 'No aliases found for this identity' : 'Search for an identity to view aliases'}
                </p>
              )}
          </Card>
          </div>
        )}

      {searchUserId && error && (
        <div className="lg:col-span-3" ref={resultRef}>
          <Card title="Error">
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <p className="text-destructive text-sm">Identity not found or error occurred</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
